import React, { useState } from "react";
import { connect } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import awsRegions from "aws-regions";

import {
  postFeatureConfiguration,
} from "../../../../store/featureConfiguration/featureConfigurationSlice";

const addonDisplayName = (addOnsName) =>
  addOnsName === "Event Insight"
    ? "Cloud Event"
    : addOnsName === "Cost Insight"
      ? "FinOps"
      : addOnsName;

const SELECT_MENU_PROPS = {
  sx: { zIndex: 1500 },
  PaperProps: {
    sx: { zIndex: 1500 },
  },
};

const filledInputSx = {
  "& .MuiFilledInput-root": {
    backgroundColor: "#f4f4f5",
    borderRadius: "10px",
    "&:before, &:after": { display: "none" },
    "&:hover": { backgroundColor: "#ececee" },
    "&.Mui-focused": {
      backgroundColor: "#f4f4f5",
      boxShadow: "0 0 0 2px rgba(17, 24, 39, 0.16)",
    },
    "&.Mui-error": { backgroundColor: "#fef2f2" },
    "&.Mui-disabled": { backgroundColor: "#f4f4f5" },
  },
  "& .MuiFilledInput-input": {
    py: 1.35,
    px: 1.5,
    fontSize: 14,
    fontWeight: 400,
  },
};

const FieldLabel = ({ children, htmlFor, required }) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    sx={{
      display: "block",
      fontSize: 13,
      fontWeight: 600,
      color: "#111827",
      mb: 0.75,
      lineHeight: 1.3,
    }}
  >
    {children}
    {required ? (
      <Box component="span" sx={{ color: "#9ca3af", ml: 0.35 }}>
        *
      </Box>
    ) : null}
  </Typography>
);

const BucketPolicyModal = (props) => {
  const [role, setRole] = useState("");
  const [bucketName, setBucketName] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [stackName, setStackName] = useState("");
  const [processingMode, setProcessingMode] = useState("");
  const [selectedMemberAccounts, setSelectedMemberAccounts] = useState([]);
  const [errors, setErrors] = useState({});
  const { accountNumber, externalId, iamrole, is_master } = props.editAccount;
  const { addOnsName } = props;
  const { accounts } = props;
  const setNotificationSent = props.setNotificationSent;

  const validateRole = (value) => {
    if (!value) {
      return "Role is required";
    } else if (!/^arn:aws:iam::[0-9]{12}:role/.test(value)) {
      return "IAM Role must be in proper format";
    }
    return "";
  };

  const validateAccountNumber = () => {
    const value = accountNumber;
    if (!value) {
      return "Account number is required";
    } else if (!/^[0-9]+$/.test(value) || value.length !== 12) {
      return "Account number must be exactly 12 digits and only numbers";
    }
    return "";
  };

  const validateBucketName = (value) => {
    if (!value) {
      return "Bucket name is required";
    } else if (!/^arn:aws:s3:::[a-z0-9]([a-z0-9.-]{1,61}[a-z0-9])?$/.test(value)) {
      return "S3 bucket name must be in proper format";
    }
    return "";
  };

  const validateSelectedRegion = (value) => {
    if (!value) {
      return "AWS Region is required";
    }
    return "";
  };

  const validateStackName = (value) => {
    const trimmed = (value || "").trim();
    if (!trimmed) {
      return "";
    }
    if (trimmed.length > 128) {
      return "Stack name must be 128 characters or fewer";
    }
    if (!/^[a-zA-Z][-a-zA-Z0-9]*$/.test(trimmed)) {
      return "Stack name must start with a letter and contain only letters, numbers, and hyphens";
    }
    return "";
  };

  const validateProcessingMode = (value) => {
    if (addOnsName === "Cost Insight" && is_master && !value) {
      return "Processing mode is required";
    }
    return "";
  };

  const validateMemberAccounts = () => {
    return "";
  };

  const handleRoleChange = (e) => {
    const value = e.target.value;
    setRole(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      role: validateRole(value),
    }));
  };

  const handleBucketNameChange = (e) => {
    const value = e.target.value;
    setBucketName(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      bucketName: validateBucketName(value),
    }));
  };

  const handleRegionChange = (e) => {
    const value = e.target.value;
    setSelectedRegion(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      selectedRegion: validateSelectedRegion(value),
    }));
  };

  const handleStackNameChange = (e) => {
    const value = e.target.value;
    setStackName(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      stackName: validateStackName(value),
    }));
  };

  const handleProcessingModeChange = (e) => {
    const value = e.target.value;
    setProcessingMode(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      processingMode: validateProcessingMode(value),
    }));
  };

  const handleMemberAccountsChange = (event) => {
    const value = event.target.value;
    setSelectedMemberAccounts(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      memberAccounts: validateMemberAccounts(value),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setNotificationSent(false);

    const roleError = validateRole(role);
    const accountNumberError = validateAccountNumber();
    const bucketNameError = validateBucketName(bucketName);
    const selectedRegionError = validateSelectedRegion(selectedRegion);
    const stackNameError = validateStackName(stackName);
    const processingModeError = validateProcessingMode(processingMode);
    const memberAccountsError = validateMemberAccounts(selectedMemberAccounts);

    if (
      !roleError &&
      !accountNumberError &&
      !bucketNameError &&
      !selectedRegionError &&
      !stackNameError &&
      !processingModeError &&
      !memberAccountsError
    ) {
      const featureConfigurationRequest = {
        role: role,
        accountNumber: accountNumber,
        bucketName: bucketName,
        readOnlyRole: iamrole,
        selectedRegion: selectedRegion,
        stackName: stackName.trim(),
        module: addOnsName,
        externalId: externalId,
      };

      if (addOnsName === "Cost Insight" && is_master) {
        featureConfigurationRequest.processingMode = processingMode;
      }

      if (addOnsName === "Event Insight" && is_master) {
        featureConfigurationRequest.memberAccountNumbers =
          selectedMemberAccounts.join(",");
      }

      props.postFeatureConfiguration(featureConfigurationRequest);
    } else {
      setErrors({
        role: roleError,
        accountNumber: accountNumberError,
        bucketName: bucketNameError,
        selectedRegion: selectedRegionError,
        stackName: stackNameError,
        processingMode: processingModeError,
        memberAccounts: memberAccountsError,
      });
    }
  };

  const validMemberAccounts =
    addOnsName === "Event Insight" && is_master
      ? accounts?.filter((acc) => {
          const isNotMaster = acc.accountNumber !== accountNumber;
          const eventInsightAddon = acc.subscription?.addons?.find(
            (addon) => addon.name === "Event Insight"
          );
          const isEventInsightDisabled =
            !eventInsightAddon || eventInsightAddon.enabled === false;
          const isNotInit = !acc.accountNumber.startsWith("INIT");
          return isNotMaster && isEventInsightDisabled && isNotInit;
        }) || []
      : [];

  return (
    <Dialog
      open={!!props.isOpen}
      onClose={(_event, reason) => {
        if (reason === "backdropClick") return;
        props.closeModal();
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "calc(100vh - 48px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: "#F98125",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.35rem",
              color: "#FFFFFF",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Verify Bucket Policy
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              color: "rgba(255,255,255,0.9)",
              fontSize: "0.875rem",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: 2.5,
              rowGap: 0.5,
            }}
          >
            <Box component="span">
              Addon:{" "}
              <Box component="span" sx={{ fontWeight: 600, color: "#FFFFFF" }}>
                {addonDisplayName(addOnsName)}
              </Box>
            </Box>
            <Box component="span">
              Account Number:{" "}
              <Box component="span" sx={{ fontWeight: 600, color: "#FFFFFF" }}>
                {accountNumber}
              </Box>
            </Box>
          </Typography>
        </Box>
        <button
          type="button"
          aria-label="Close"
          onClick={props.closeModal}
          style={{
            padding: 0,
            margin: 0,
            border: "none",
            background: "transparent",
            color: "#FFFFFF",
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            opacity: 1,
            boxShadow: "none",
            flexShrink: 0,
          }}
        >
          <span aria-hidden="true">×</span>
        </button>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          flex: 1,
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 2.5,
            pb: 2,
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box>
            <FieldLabel htmlFor="bucket-policy-role" required>
              Role ARN
            </FieldLabel>
            <TextField
              id="bucket-policy-role"
              fullWidth
              hiddenLabel
              variant="filled"
              size="small"
              value={role}
              onChange={handleRoleChange}
              error={!!errors.role}
              helperText={errors.role}
              placeholder="arn:aws:iam::123456789012:role/..."
              InputProps={{ disableUnderline: true }}
              sx={filledInputSx}
            />
          </Box>

          <Box>
            <FieldLabel htmlFor="bucket-policy-bucket" required>
              S3 Bucket ARN
            </FieldLabel>
            <TextField
              id="bucket-policy-bucket"
              fullWidth
              hiddenLabel
              variant="filled"
              size="small"
              value={bucketName}
              onChange={handleBucketNameChange}
              error={!!errors.bucketName}
              helperText={errors.bucketName}
              placeholder="arn:aws:s3:::bucket-name"
              InputProps={{ disableUnderline: true }}
              sx={filledInputSx}
            />
          </Box>

          <Box>
            <FieldLabel htmlFor="bucket-policy-region" required>
              S3 AWS Region
            </FieldLabel>
            <FormControl fullWidth error={!!errors.selectedRegion} sx={filledInputSx}>
              <Select
                id="bucket-policy-region"
                displayEmpty
                variant="filled"
                value={selectedRegion}
                onChange={handleRegionChange}
                disableUnderline
                MenuProps={SELECT_MENU_PROPS}
                sx={{
                  borderRadius: "10px",
                  "& .MuiSelect-select": {
                    py: 1.35,
                    px: 1.5,
                    fontSize: 14,
                  },
                }}
              >
                <MenuItem value="">
                  <em>Select AWS Region</em>
                </MenuItem>
                {awsRegions.list().map((region) => (
                  <MenuItem key={region.code} value={region.code}>
                    {region.name} — {region.code}
                  </MenuItem>
                ))}
              </Select>
              {errors.selectedRegion ? (
                <FormHelperText>{errors.selectedRegion}</FormHelperText>
              ) : null}
            </FormControl>
          </Box>

          <Box>
            <FieldLabel htmlFor="bucket-policy-stack">
              Stack Name (Optional)
            </FieldLabel>
            <TextField
              id="bucket-policy-stack"
              fullWidth
              hiddenLabel
              variant="filled"
              size="small"
              value={stackName}
              onChange={handleStackNameChange}
              error={!!errors.stackName}
              helperText={errors.stackName}
              placeholder="my-cloudcatcher-stack"
              inputProps={{ maxLength: 128 }}
              InputProps={{ disableUnderline: true }}
              sx={filledInputSx}
            />
          </Box>

          {addOnsName === "Cost Insight" && is_master && (
            <Box>
              <FieldLabel htmlFor="bucket-policy-processing" required>
                Processing Mode
              </FieldLabel>
              <FormControl
                fullWidth
                error={!!errors.processingMode}
                sx={filledInputSx}
              >
                <Select
                  id="bucket-policy-processing"
                  displayEmpty
                  variant="filled"
                  value={processingMode}
                  onChange={handleProcessingModeChange}
                  disableUnderline
                  MenuProps={{
                    ...SELECT_MENU_PROPS,
                    PaperProps: {
                      ...SELECT_MENU_PROPS.PaperProps,
                      style: { minWidth: 320 },
                    },
                  }}
                  sx={{
                    borderRadius: "10px",
                    "& .MuiSelect-select": {
                      py: 1.35,
                      px: 1.5,
                      fontSize: 14,
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Select Processing Mode</em>
                  </MenuItem>
                  <MenuItem value="MASTER_AND_ALL_MEMBERS">
                    All Organization accounts
                  </MenuItem>
                  <MenuItem value="MASTER_AND_ONBOARDED_MEMBERS_ONLY">
                    Onboarded Accounts Only
                  </MenuItem>
                </Select>
                {errors.processingMode ? (
                  <FormHelperText>{errors.processingMode}</FormHelperText>
                ) : null}
              </FormControl>
            </Box>
          )}

          {validMemberAccounts.length > 0 && (
            <Box>
              <FieldLabel htmlFor="bucket-policy-members">
                Member Accounts
              </FieldLabel>
              <FormControl
                fullWidth
                error={!!errors.memberAccounts}
                sx={filledInputSx}
              >
                <Select
                  id="bucket-policy-members"
                  multiple
                  displayEmpty
                  variant="filled"
                  value={selectedMemberAccounts}
                  onChange={handleMemberAccountsChange}
                  disableUnderline
                  renderValue={(selected) =>
                    selected.length ? selected.join(", ") : "Select member accounts"
                  }
                  MenuProps={SELECT_MENU_PROPS}
                  sx={{
                    borderRadius: "10px",
                    "& .MuiSelect-select": {
                      py: 1.35,
                      px: 1.5,
                      fontSize: 14,
                    },
                  }}
                >
                  {validMemberAccounts.map((account) => (
                    <MenuItem
                      key={account.accountNumber}
                      value={account.accountNumber}
                    >
                      {account.accountName} ({account.accountNumber})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.memberAccounts}
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      fontSize: "0.75rem",
                      color: "#64748b",
                      mt: 0.5,
                      lineHeight: 1.4,
                    }}
                  >
                    Please make sure you have already created replication role
                    for all the member accounts you want to enable Cloud Event
                    for and they should be linked to this Master Account
                  </Box>
                </FormHelperText>
              </FormControl>
            </Box>
          )}
        </Box>

        <Divider />
        <Box
          sx={{
            px: 3,
            py: 2,
            flexShrink: 0,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={props.isPostingFeatureConfiguration}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              px: 3,
              borderRadius: 2,
              boxShadow: "none",
              backgroundColor: "#F98125",
              "&:hover": { backgroundColor: "#E07015", boxShadow: "none" },
              "&.Mui-disabled": { backgroundColor: "#ccc", color: "#fff" },
            }}
          >
            {props.isPostingFeatureConfiguration ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Submit"
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

const mapStateToProps = (state) => {
  const {
    isPostingFeatureConfiguration,
    postFeatureConfigurationMessage,
    postFeatureConfigurationError,
  } = state.featureConfiguration;
  return {
    isPostingFeatureConfiguration,
    postFeatureConfigurationMessage,
    postFeatureConfigurationError,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    postFeatureConfiguration: (featureConfigurationPayload) => {
      dispatch(postFeatureConfiguration(featureConfigurationPayload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BucketPolicyModal);
